import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { interval, Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { I18NextPipe } from 'angular-i18next';
import { finalize } from 'rxjs/operators';
import { DialogHelpComponent } from 'src/app/common/dialogs/dialog-help/dialog-help.component';
import { DialogDeleteNewComponent } from 'src/app/common/dialogs/dialog-delete-new/dialog-delete-new.component';

import { realTimeService } from 'src/app/services/services/realtime.service';
import { SettingsService } from 'src/app/services/services/settings.service';

export interface OngoingRequestRow {
  requestId: number;

  queueName: string;

  requestDescription?: string;          // full
  shortDescription?: string;            // truncated for cell

  formattedType: string;
  bColorType?: string;                  // bg color for type pill
  fColorType?: string;                  // fg color for type pill

  requestSource?: string;               // “number” column in old view

  // Connected duration
  connectedDuration?: number | null;    // seconds
  formattedConnectedDuration: string;

  userName?: string | null;

  userStatusId: number;                 // for “connected” count (5 in old)
  statusIcon?: string;
  formattedStatus: string;

  // Time in status
  timeInStatus?: number | null;         // seconds
  formattedTimeInStatus: string;
  contactCentreStatus?: number | null;   
  // permissions
  finalDelete?: boolean;                // old: FinalDelete -> enabled checkbox only if true
}

@Component({
    selector: 'app-ongoing-requests',
    templateUrl: './ongoing-requests.component.html',
    styleUrls: ['./ongoing-requests.component.scss'],
    standalone: false
})
export class OngoingRequestsComponent implements OnInit, OnDestroy {
  // Columns (keep old order)
  displayedColumns: string[] = [
    'queueName',
    'requestDescription',
    'type',
    'requestSource',
    'connected',
    'agent',
    'agentStatus',
    'statusDuration',
    // 'select' (conditionally added below)
  ];

  BreadcrumbsLink = [
    { "label": "Home", "url": "/" },
    { "label": "Real-Time", "url": "/real-time/overview" },
    { "label": "Ongoing Requests", "url": "/real-time/ongoing-requests" }];

  dataSource = new MatTableDataSource<OngoingRequestRow>([]);
  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  // UI state
  isLoading = true;
  filter = '';
  connectedCount = 0;
  errorMessage: string | null = null;
  // Flags / config (from settings)
  pollingIntervalMs = 10000;      // Settings.AjaxRefreshInterval (ms)
  propertyAllowDelete = true;     // Settings.PropertyAllowDelete
  maxDeleteRequest = 100;         // Optional: Settings.MaxDeleteRequest (if provided)

  // Selection
  selected: Record<number, boolean> = {};
  get anySelected(): boolean {
    return Object.values(this.selected).some(Boolean);
  }
  get anySelectable(): boolean {
    return (this.dataSource.data || []).filter(r => !!r.finalDelete).length > 0;
  }
  get allSelectableIds(): number[] {
    return (this.dataSource.data || [])
      .filter(r => !!r.finalDelete)   // only those that can be deleted
      .map(r => r.requestId);
  }
  get allSelected(): boolean {
    const ids = this.allSelectableIds;
    return ids.length > 0 && ids.every(id => !!this.selected[id]);
  }

  // Polling
  private pollingSub?: Subscription;

  constructor(
    private dialog: MatDialog,
    private realtimeService: realTimeService,
    private i18n: I18NextPipe,
    private settingsService: SettingsService
  ) {}

  async ngOnInit(): Promise<void> {
    // ⬇️ Load realtime settings first
    const cfg: any = await this.settingsService.realtimeSettings();
    this.propertyAllowDelete = !!cfg?.propertyAllowDelete;
    this.pollingIntervalMs = this.normalizeInterval(cfg?.ajaxRefreshInterval);
    if (cfg?.maxDeleteRequest != null) {
      const n = Number(cfg.maxDeleteRequest);
      if (!Number.isNaN(n) && n > 0) this.maxDeleteRequest = n;
    }

    if (this.propertyAllowDelete && !this.displayedColumns.includes('select')) {
  this.displayedColumns = ['select', ...this.displayedColumns];
}
    // Sorting: use numeric seconds where available
    this.dataSource.sortingDataAccessor = (row: OngoingRequestRow, column: string) => {
      switch (column) {
        case 'queueName': return (row.queueName || '').toLowerCase();
        case 'requestDescription': return (row.requestDescription || '').toLowerCase();
        case 'type': return (row.formattedType || '').toLowerCase();
        case 'requestSource': return (row.requestSource || '').toLowerCase();
        case 'connected': return row.connectedDuration ?? -1;
        case 'agent': return (row.userName || '').toLowerCase();
        case 'agentStatus': return (row.formattedStatus || '').toLowerCase();
        case 'statusDuration': return row.timeInStatus ?? -1;
        default: return '';
      }
    };
    this.dataSource.sort = this.sort;

    // Filter
    this.dataSource.filterPredicate = (row, text) => {
      const f = (text || '').trim().toLowerCase();
      if (!f) return true;
      return [
        row.queueName,
        row.requestDescription,
        row.formattedType,
        row.requestSource,
        row.formattedConnectedDuration,
        row.userName ?? '—',
        row.formattedStatus,
        row.formattedTimeInStatus
      ].some(v => (v || '').toLowerCase().includes(f));
    };

    this.loadData(true);
    this.startPolling();
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  // --- Data ---
  private loadData(showLoader = false): void {
    if (this.anySelected) return;  // pause auto-refresh when selecting
    if (showLoader) this.isLoading = true;

    this.realtimeService
      .getOngoingCallList()
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(
        (rows) => {
          this.applyRows(rows ?? []);
        },
        _err => {
          // keep previous data
        }
      );
  }

  private applyRows(rows: OngoingRequestRow[]): void {
    // connected agents = statusId 5 (per old code)

    this.connectedCount = rows.reduce((acc, r) => acc + (r.userStatusId === 5 ? 1 : 0), 0);

    // compute short description for tooltip behavior
    rows.forEach(r => {
      const desc = r.requestDescription ?? '‒';
      r.shortDescription = desc.length > 30 ? `${desc.slice(0, 30)}...` : desc;
      // derive formatted durations if missing
      if (!r.formattedConnectedDuration) {
        r.formattedConnectedDuration = this.formatDuration(r.connectedDuration ?? null);
      }
      if (!r.formattedTimeInStatus) {
        r.formattedTimeInStatus = this.formatDuration(r.timeInStatus ?? null);
      }
    });

    this.dataSource.data = rows;
    if (!this.anySelected) this.selected = {};
  }

  // --- Polling ---
  private startPolling(): void {
    this.stopPolling();
    this.pollingSub = interval(this.pollingIntervalMs).subscribe(() => {
      if (!this.anySelected) this.loadData(false);
    });
  }
  private stopPolling(): void {
    this.pollingSub?.unsubscribe();
    this.pollingSub = undefined;
  }

  private normalizeInterval(raw: unknown): number {
    let ms = Number(raw) || 10000;          // default 10s
    if (ms > 0 && ms <= 60) ms *= 1000;     // treat small numbers as seconds
    return Math.max(1000, ms);              // clamp to >= 1s
  }

  // --- Filter handlers ---
  applyFilterFromProperty() {
    this.dataSource.filter = (this.filter || '').trim().toLowerCase();
  }
  clearFilter() {
    this.filter = '';
    this.dataSource.filter = '';
  }

  // --- Selection ---
  toggleSelectAll(checked: boolean) {
    const next = { ...this.selected };
    let count = Object.values(next).filter(Boolean).length;

    this.allSelectableIds.forEach(id => {
      if (checked) {
        if (!next[id] && count < this.maxDeleteRequest) {
          next[id] = true;
          count++;
        }
      } else {
        next[id] = false;
      }
    });

    this.selected = next;
    this.handleAutoRefreshState();
  }

  toggleOne(id: number, checked: boolean) {
    const currentCount = Object.values(this.selected).filter(Boolean).length;
    if (checked && currentCount >= this.maxDeleteRequest) {
      // silently ignore extra beyond the limit (old behavior)
      return;
    }
    this.selected = { ...this.selected, [id]: checked };
    this.handleAutoRefreshState();
  }

  private handleAutoRefreshState() {
    if (this.anySelected) this.stopPolling();
    else this.startPolling();
  }

  // --- Actions ---
  onDeleteSelected(): void {
    const ids = Object.keys(this.selected)
      .map(k => Number(k))
      .filter(k => this.selected[k]);

    if (!ids.length) return;

    const ref = this.dialog.open(DialogDeleteNewComponent, {
      disableClose: true,
      data: {
        title: this.i18n.transform('users:shared_deleteConfirm', { defaultValue: 'Delete confirmation' }),
        showTitleInHeader: true,
        additionalText: this.i18n.transform('realtime:queuedetails_deleteNote', { defaultValue: 'Are you sure…' }),
        confirmText: this.i18n.transform('Yes'),
        cancelText: this.i18n.transform('No'),
      }
    });

    ref.afterClosed().subscribe(confirm => {
      if (!confirm) return;

      this.isLoading = true;
      this.stopPolling();

      this.realtimeService.OngoingRequestDelete(ids)
        .pipe(finalize(() => {
          this.isLoading = false;
          this.startPolling();
        }))
        .subscribe(
          (res: any) => {
            if (res?.error) {
              const key ='realtime:' + String(res.message || 'shared_err_genericServerError');
               this.errorMessage =  this.i18n.transform(key);
              return;
            }
            this.selected = {};
            this.loadData(true);
          },
          _err => {
            this.errorMessage =this.i18n.transform('realtime:shared_err_genericServerError');
          }
        );
    });
  }

  openHelp(): void {
    this.dialog.open(DialogHelpComponent, {
      panelClass: 'dialog-help-pannel',
      backdropClass: 'backdrop-invisible',
      width: '600px',
      data: {
        title: this.i18n.transform('realtime:help_title_ongoingCall'),
        text: this.i18n.transform('realtime:help_ongoingCall'),
        renderAsHtml: true
      }
    });
  }

  private formatDuration(seconds: number | null | undefined): string {
    if (seconds == null || Number.isNaN(seconds)) return '—';
    const s = Math.round(seconds);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const ss = s % 60;
    if (h > 0) return `${h}h ${m}m ${ss}s`;
    if (m > 0) return `${m}m ${ss}s`;
    return `${ss}s`;
  }
}
